/**
 * El normalizador de provincias del censo (respuestas del cliente 2026-09-17, P-32: «Zona =
 * provincia [...] Cógelo tal cual, corrige el censo con él, y que el mapa no vuelva a descartar
 * en silencio»). Motor puro (ADR-0017): sin red, sin Prisma — el llamante decide qué hacer con
 * cada resultado, esta función solo clasifica un nombre.
 *
 * Fuente de los 52 nombres oficiales y de la tabla de alias:
 * `respuestas-tory-2026-09-17/respuestas-tory-2026-09-17/02-datos-y-catalogos/nombres-de-provincia.md`
 * (§1 «Los 52, con su código»; §2 «Los alias que trae el censo»). Las claves se guardan en
 * MINÚSCULAS (`ES-AB` → `es-ab`): es la convención que ya usa `mapaEspana.paths.ts`
 * (`clave: 'es-ab'`, código ISO 3166-2:ES en minúsculas), no la del fichero del cliente, que las
 * trae en mayúsculas — un mismo dato con dos casings distintos según quién lo consuma sería la
 * fuente de un bug silencioso el día que alguien comparara claves de sitios distintos.
 *
 * P-32 también pide normalizar acentos a NFC: «la descomposición de Apple hace que "Ávila" no
 * sea igual a "Ávila" aunque se lean idénticas» — un carácter con tilde puede llegar precompuesto
 * (NFC, un único code point) o descompuesto (NFD, letra + diacrítico combinante); son visualmente
 * idénticos pero distintos byte a byte, así que una comparación de igualdad de cadenas sin
 * normalizar los trataría como valores distintos. `normalizarProvinciaCenso` aplica
 * `.normalize('NFC')` (y `.trim()`) al nombre de entrada antes de buscarlo.
 */

/** Una provincia oficial: su código ISO 3166-2:ES en minúsculas y su nombre oficial. */
export interface ProvinciaOficial {
	/** Código ISO 3166-2:ES en minúsculas (`'es-ab'`) — la convención de `mapaEspana.paths.ts`. */
	clave: string;
	nombreOficial: string;
}

/**
 * Los 52 nombres oficiales con su código ISO 3166-2:ES (nombres-de-provincia.md §1), en el mismo
 * orden que trae el fichero del cliente.
 */
const PROVINCIAS_OFICIALES: readonly ProvinciaOficial[] = [
	{ clave: 'es-a', nombreOficial: 'Alicante' },
	{ clave: 'es-ab', nombreOficial: 'Albacete' },
	{ clave: 'es-al', nombreOficial: 'Almería' },
	{ clave: 'es-av', nombreOficial: 'Ávila' },
	{ clave: 'es-b', nombreOficial: 'Barcelona' },
	{ clave: 'es-ba', nombreOficial: 'Badajoz' },
	{ clave: 'es-bi', nombreOficial: 'Vizcaya' },
	{ clave: 'es-bu', nombreOficial: 'Burgos' },
	{ clave: 'es-c', nombreOficial: 'A Coruña' },
	{ clave: 'es-ca', nombreOficial: 'Cádiz' },
	{ clave: 'es-cc', nombreOficial: 'Cáceres' },
	{ clave: 'es-ce', nombreOficial: 'Ceuta' },
	{ clave: 'es-co', nombreOficial: 'Córdoba' },
	{ clave: 'es-cr', nombreOficial: 'Ciudad Real' },
	{ clave: 'es-cs', nombreOficial: 'Castellón' },
	{ clave: 'es-cu', nombreOficial: 'Cuenca' },
	{ clave: 'es-gc', nombreOficial: 'Las Palmas' },
	{ clave: 'es-gi', nombreOficial: 'Girona' },
	{ clave: 'es-gr', nombreOficial: 'Granada' },
	{ clave: 'es-gu', nombreOficial: 'Guadalajara' },
	{ clave: 'es-h', nombreOficial: 'Huelva' },
	{ clave: 'es-hu', nombreOficial: 'Huesca' },
	{ clave: 'es-j', nombreOficial: 'Jaén' },
	{ clave: 'es-l', nombreOficial: 'Lleida' },
	{ clave: 'es-le', nombreOficial: 'León' },
	{ clave: 'es-lo', nombreOficial: 'La Rioja' },
	{ clave: 'es-lu', nombreOficial: 'Lugo' },
	{ clave: 'es-m', nombreOficial: 'Madrid' },
	{ clave: 'es-ma', nombreOficial: 'Málaga' },
	{ clave: 'es-ml', nombreOficial: 'Melilla' },
	{ clave: 'es-mu', nombreOficial: 'Murcia' },
	{ clave: 'es-na', nombreOficial: 'Navarra' },
	{ clave: 'es-o', nombreOficial: 'Asturias' },
	{ clave: 'es-or', nombreOficial: 'Ourense' },
	{ clave: 'es-p', nombreOficial: 'Palencia' },
	{ clave: 'es-pm', nombreOficial: 'Baleares' },
	{ clave: 'es-po', nombreOficial: 'Pontevedra' },
	{ clave: 'es-s', nombreOficial: 'Cantabria' },
	{ clave: 'es-sa', nombreOficial: 'Salamanca' },
	{ clave: 'es-se', nombreOficial: 'Sevilla' },
	{ clave: 'es-sg', nombreOficial: 'Segovia' },
	{ clave: 'es-so', nombreOficial: 'Soria' },
	{ clave: 'es-ss', nombreOficial: 'Guipúzcoa' },
	{ clave: 'es-t', nombreOficial: 'Tarragona' },
	{ clave: 'es-te', nombreOficial: 'Teruel' },
	{ clave: 'es-tf', nombreOficial: 'Santa Cruz de Tenerife' },
	{ clave: 'es-to', nombreOficial: 'Toledo' },
	{ clave: 'es-v', nombreOficial: 'Valencia' },
	{ clave: 'es-va', nombreOficial: 'Valladolid' },
	{ clave: 'es-vi', nombreOficial: 'Álava' },
	{ clave: 'es-z', nombreOficial: 'Zaragoza' },
	{ clave: 'es-za', nombreOficial: 'Zamora' },
];

/**
 * Los alias que trae el censo, cada uno a la clave de su provincia oficial
 * (nombres-de-provincia.md §2 — cada fila del documento con varias formas separadas por "/" se
 * despliega aquí en una entrada por forma). Las islas se agrupan (regla 2 del documento): Ibiza,
 * Mallorca y Menorca son Baleares; Gran Canaria, Lanzarote y Fuerteventura son Las Palmas;
 * Tenerife es Santa Cruz de Tenerife.
 */
const ALIASES_CENSO: ReadonlyArray<readonly [alias: string, clave: string]> = [
	['La Coruña', 'es-c'],
	['Coruña, A', 'es-c'],
	['Gerona', 'es-gi'],
	['Lérida', 'es-l'],
	['Orense', 'es-or'],
	['Avila', 'es-av'],
	['Alava', 'es-vi'],
	['Araba', 'es-vi'],
	['Guipuzcoa', 'es-ss'],
	['Gipuzkoa', 'es-ss'],
	['Vizcaya', 'es-bi'],
	['Bizkaia', 'es-bi'],
	['Ibiza', 'es-pm'],
	['Eivissa', 'es-pm'],
	['Mallorca', 'es-pm'],
	['Palma de Mallorca', 'es-pm'],
	['Menorca', 'es-pm'],
	['Islas Baleares', 'es-pm'],
	['Tenerife', 'es-tf'],
	['Gran Canaria', 'es-gc'],
	['Las Palmas de Gran Canaria', 'es-gc'],
	['Lanzarote', 'es-gc'],
	['Fuerteventura', 'es-gc'],
	['La Rioja', 'es-lo'],
	['Logroño', 'es-lo'],
	['Asturias', 'es-o'],
	['Oviedo', 'es-o'],
	['Gijón', 'es-o'],
	['Cantabria', 'es-s'],
	['Santander', 'es-s'],
];

/**
 * Las seis filas que la tabla del cliente NO cubre (P-32, respondida en el hilo de esta misma
 * tarea, no en el documento): `El Escorial` es un municipio de Madrid, así que resuelve a
 * Madrid; las otras cinco no tienen provincia española — quedan "sin zona", nunca descartadas en
 * silencio.
 */
const ALIAS_SIN_COBERTURA_DEL_CLIENTE: ReadonlyArray<readonly [alias: string, clave: string]> = [
	['El Escorial', 'es-m'],
];

/** Nombres del censo que se reconocen pero no tienen provincia española: quedan "sin zona". */
const CENSO_SIN_ZONA: readonly string[] = ['Otro', 'Argentina', 'Colombia', 'México', 'Venezuela'];

const normalizarTexto = (texto: string): string => texto.normalize('NFC').trim();

/**
 * Busca la provincia oficial de `clave`, o lanza si no existe — nunca debería ocurrir con los
 * datos declarados en este mismo fichero: si ocurriera, es un alias mal escrito a mano, y falla
 * en el arranque en vez de perder el alias en silencio (mismo espíritu que P-32, regla 1,
 * aplicado a un error del propio fichero, no del censo).
 */
const buscarOficialPorClave = (alias: string, clave: string): ProvinciaOficial => {
	const oficial = PROVINCIAS_OFICIALES.find(provincia => provincia.clave === clave);
	if (oficial === undefined) {
		throw new Error(`rules/provincias.ts: el alias "${alias}" apunta a una clave desconocida ("${clave}")`);
	}
	return oficial;
};

const MAPA_NOMBRE_A_OFICIAL: ReadonlyMap<string, ProvinciaOficial> = new Map([
	...PROVINCIAS_OFICIALES.map(provincia => [normalizarTexto(provincia.nombreOficial), provincia] as const),
	...[...ALIASES_CENSO, ...ALIAS_SIN_COBERTURA_DEL_CLIENTE].map(
		([alias, clave]) => [normalizarTexto(alias), buscarOficialPorClave(alias, clave)] as const,
	),
]);

const CONJUNTO_CENSO_SIN_ZONA: ReadonlySet<string> = new Set(CENSO_SIN_ZONA.map(normalizarTexto));

/**
 * Los tres desenlaces de normalizar un nombre del censo (P-32, regla 1: «Nada se descarta en
 * silencio. Un nombre que no case con nada se queda en un listado de "sin zona" que alguien
 * mira, no se tira»): `reconocida` trae la provincia oficial resuelta; `sinZona` es un nombre
 * reconocido que a propósito no tiene provincia española (`Otro`, las cuatro extranjeras, y
 * cualquier alias que en el futuro se declare así); `noReconocida` es un nombre que no casa con
 * ningún nombre oficial ni alias — el llamante puede entonces registrarlo para revisión manual,
 * en vez de perderlo silenciosamente en un `undefined` indistinguible de "sin zona".
 */
export type ResultadoNormalizarProvincia =
	| ({ estado: 'reconocida' } & ProvinciaOficial)
	| { estado: 'sinZona' }
	| { estado: 'noReconocida' };

/**
 * Normaliza un nombre de provincia del censo a su provincia oficial (P-32). Aplica NFC + trim
 * antes de comparar (regla del documento: «normalizad los acentos a NFC»), y distingue "no
 * reconocida" de "sin zona" en vez de colapsar ambas en `undefined` — el llamante puede entonces
 * decidir qué hacer con cada una (revisar a mano lo no reconocido; dejar sin zona lo que a
 * propósito no la tiene).
 */
export const normalizarProvinciaCenso = (nombreCenso: string): ResultadoNormalizarProvincia => {
	const normalizado = normalizarTexto(nombreCenso);
	if (CONJUNTO_CENSO_SIN_ZONA.has(normalizado)) {
		return { estado: 'sinZona' };
	}
	const oficial = MAPA_NOMBRE_A_OFICIAL.get(normalizado);
	return oficial === undefined ? { estado: 'noReconocida' } : { estado: 'reconocida', ...oficial };
};
